import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { PhoneApi } from "../../api/PhoneApi";
import type { CreatePhone } from "../../generated/dto/CreatePhone";
import type { DeletePhoneStaff } from "../../generated/dto/DeletePhoneStaff";
import type { PhoneSearchParams } from "../../generated/dto/PhoneSearchParams";
import type { PhoneStaff } from "../../generated/dto/PhoneStaff";
import type { PhoneStaffSearchResult } from "../../generated/dto/PhoneStaffSearchResult";
import type { UpdatePhone } from "../../generated/dto/UpdatePhone";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class PhoneApiClient implements PhoneApi {
  constructor(private readonly client = useClient()) {}

  async getPhoneStaff(id: number): Promise<PhoneStaff> {
    const { data } = await this.client.get<unknown>(`/phone/staff/${id}`);
    return deserializeCbiDto<PhoneStaff>(data, "generated.dto.PhoneStaff");
  }

  async createPhoneStaff(source: CreatePhone): Promise<PhoneStaff> {
    const { data } = await this.client.post<unknown>(`/phone/staff`, serializeCbiDto(source, "generated.dto.CreatePhone"));
    return deserializeCbiDto<PhoneStaff>(data, "generated.dto.PhoneStaff");
  }

  async updatePhoneStaff(source: UpdatePhone): Promise<PhoneStaff> {
    const { data } = await this.client.put<unknown>(`/phone/staff/${source.phoneId}`, serializeCbiDto(source, "generated.dto.UpdatePhone"));
    return deserializeCbiDto<PhoneStaff>(data, "generated.dto.PhoneStaff");
  }

  async deletePhoneStaff(source: DeletePhoneStaff): Promise<void> {
    await this.client.delete(`/phone/staff/${source.phoneId}`);
  }

  async searchPhoneStaff(params: PhoneSearchParams, page: number, perPage: number): Promise<PaginationResult<PhoneStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/phone/staff/search`, serializeCbiDto(params, "generated.dto.PhoneSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PhoneStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<PhoneStaffSearchResult>>(result.items, "generated.dto.PhoneStaffSearchResult") };
    return result;
  }

  async searchPhoneStaffFull(params: PhoneSearchParams, page: number, perPage: number): Promise<PaginationResult<PhoneStaff>> {
    const { data } = await this.client.post<unknown>(`/phone/staff/search/full`, serializeCbiDto(params, "generated.dto.PhoneSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<PhoneStaff>;
    result = { ...result, items: deserializeCbiDto<Array<PhoneStaff>>(result.items, "generated.dto.PhoneStaff") };
    return result;
  }
}

export const usePhoneApi = (): PhoneApi => new PhoneApiClient();
